import { AfterViewInit, ChangeDetectorRef, Component, effect, Input, OnInit } from '@angular/core';
import { ViewChild, ElementRef } from '@angular/core';
import { PatientSessionService } from '../patient-session.service';
import { PatientTargetDataModel, PatientTargetModel, PatientTargetStatus } from '../../../core/api/models';
import { BaseBoxComponent } from '../base-box/base-box.component';
import { AlertService } from '../../../shared/services/alert.service';

@Component({
  selector: 'app-box-anecdotal',
  templateUrl: './box-anecdotal.component.html',
  standalone: false,
})
export class BoxAnecdotalComponent extends BaseBoxComponent {
  @Input() isTopBox: boolean = true;
  @Input() isBottomBox: boolean = false;
  @Input() target: PatientTargetModel | undefined = undefined;
  @Input() isPreview?: boolean;
  expectedAmount: number = 0;
  targetID: number = 0;
  anecdotals: any[] = [];
  remainingTrials: number = 0;

  currentAnecdotalIndex: number = 0;
  targetData: PatientTargetDataModel[] = [];

  timeoutSaveAnecdotal: any;
  textToSave: string = '';

  constructor(
    protected override cdRef: ChangeDetectorRef,
    private patientSessionService: PatientSessionService,
    private alertService: AlertService
  ) {
    super(cdRef);
    effect(() => {
      this.patientSessionService.dataChanged();
      const targetData = this.patientSessionService.getTargetData(this.targetID);
      if (targetData) {
        this.targetData = targetData.filter(t => !t.DeletedDate);

        this.remainingTrials = (this.target?.DesiredTrials ?? 0) - this.targetData.length;
        if (this.remainingTrials < 0) {
          this.remainingTrials = 0;
        }

        if (this.target?.TargetStatus === PatientTargetStatus.Mastered || this.target?.TargetStatus === PatientTargetStatus.Closed || this.target?.TargetStatus === PatientTargetStatus.Fluent) {
          this.remainingTrials = 0;
        }

        // Create anecdotals array with unique entries - prevent duplication
        const uniqueAnecdotals = new Map();

        this.targetData.forEach(t => {
          // Use RecordedDate as the unique key to avoid duplicates
          if (t.RecordedDate) {
            uniqueAnecdotals.set(t.RecordedDate, {
              ID: t.ID,
              text: t.TextValue,
              date: this.formatDate(new Date(t.RecordedDate)),
              RecordedDate: t.RecordedDate,
              DeletedDate: t.DeletedDate
            });
          }
        });

        // Convert the Map values back to an array
        this.anecdotals = Array.from(uniqueAnecdotals.values());

        // Sort by date (most recent first)
        this.anecdotals.sort((a, b) => {
          const dateA = new Date(a.RecordedDate || '');
          const dateB = new Date(b.RecordedDate || '');
          return dateB.getTime() - dateA.getTime();
        });

        // Set the current index to the most recent entry
        if (this.anecdotals.length > 0) {
          this.currentAnecdotalIndex = 0;
        }
      }
      else {
        this.targetData = [];
        this.anecdotals = [];
      }
    });
  }


  ngOnInit() {
    this.expectedAmount = this.target?.DesiredTrials ?? 1;
    this.targetID = this.target?.ID ?? 0;
  }


  addOrOpenAnecdotal() {
    if (this.anecdotals.length > 0) {
      this.showExtra();
    } else {
      this.addAnecdotal();
      this.showExtra();
    }
  }

  addAnecdotal() {
    const currentDate = new Date();
    const formattedDate = this.formatDate(currentDate);

    // Create a unique timestamp-based ID for the new anecdotal entry
    const timestamp = currentDate.getTime().toString();
    const recordedDate = currentDate.toServerString();

    // Check if an anecdotal with this timestamp already exists to avoid duplication
    const existingIndex = this.anecdotals.findIndex(a => a.RecordedDate === recordedDate);

    if (existingIndex >= 0) {
      // If it exists, select it instead of creating a duplicate
      this.currentAnecdotalIndex = existingIndex;
    } else {
      // Create a new anecdotal entry
      this.anecdotals.unshift({ text: '', date: formattedDate, RecordedDate: recordedDate });
      this.currentAnecdotalIndex = 0;
    }
  }

  saveAnecdotal() {
    // if (this.timeoutSaveAnecdotal) {
    //   clearTimeout(this.timeoutSaveAnecdotal);
    // }
    const currentAnecdotal = this.currentAnecdotal();
    this.textToSave = currentAnecdotal.text;
    // this.timeoutSaveAnecdotal = setTimeout(() => {
    // console.log('Saving anecdotal entry with text:', this.textToSave);

    // const currentAnecdotal = this.currentAnecdotal();
    // currentAnecdotal.text = text;

    // Look for existing target data with the same record date
    let targetDataToSave: PatientTargetDataModel | undefined = this.targetData.find(t =>
      t.RecordedDate === currentAnecdotal.RecordedDate
    );

    if (targetDataToSave) {
      console.log('Updating existing anecdotal data:', targetDataToSave);
      targetDataToSave.TextValue = this.textToSave;
      this.patientSessionService.saveTargetData(targetDataToSave);
    } else {
      console.log('Creating new anecdotal data for date:', currentAnecdotal.RecordedDate);
      targetDataToSave = {
        IntValue: null,
        PatientSessionID: 0,
        PatientTargetID: this.targetID,
        RecordedDate: currentAnecdotal.RecordedDate,
        TextValue: this.textToSave
      };
      this.patientSessionService.addTargetData(targetDataToSave);
    }
  // }, 1);
  }

  currentAnecdotal() {
    if (this.anecdotals.length == 0) {
      return { text: '', date: '' };
    }
    return this.anecdotals[this.currentAnecdotalIndex];
  }

  selectAnecdotal(index: number) {
    this.currentAnecdotalIndex = index;
  }

  override doLongPress(): void {
    this.showExtra();
  }

  deleteAnecdotal(anecdotal: any) {
    //confirm the deletion
    const confirmed = this.alertService.confirm('Are you sure you want to delete this anecdotal?', 'Delete Anecdotal');
    confirmed.subscribe(result => {
      if (result) {
        let targetDataToSave: PatientTargetDataModel | undefined = this.targetData.find(t =>
          t.RecordedDate === anecdotal.RecordedDate
        );

        if (targetDataToSave) {
          console.log('Updating existing anecdotal data:', targetDataToSave);
          targetDataToSave.DeletedDate = new Date().toServerString();

          this.patientSessionService.saveTargetData(targetDataToSave);
        }
      }
    });
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (this.timeoutSaveAnecdotal && this.textToSave) {
      this.saveAnecdotal();
    }
  }
}